import { EnemyId } from "../state/runState";
import type { PerkId } from "./perks";

/**
 * BOSS ENCOUNTER FLOW DOCUMENTATION
 *
 * The boss encounter system follows this flow:
 *
 * 1. FLOOR COMPLETION DETECTION
 *    - When player clears all regular encounters on a floor (encountersRemaining === 0)
 *    - Floor clear detection triggers in combat victory handler
 *    - State flag isBossEncounter is set to true
 *
 * 2. BOSS PROMPT SCREEN
 *    - ScreenManager routes to BOSS_PROMPT screen
 *    - Displays "Floor Complete! Boss Approaching..." message
 *    - Shows boss name and preview (from floorConfig.bossCandidate)
 *    - Player confirms to proceed to boss fight
 *
 * 3. BOSS SELECTION & ASSIGNMENT
 *    - Boss ID is retrieved from currentFloorConfig.bossCandidate
 *    - BossFactory.create() generates boss instance with:
 *      - Base stats from BossDef
 *      - Floor-based scaling (1.5x multiplier vs regular enemies)
 *      - Boss-specific perks applied
 *
 * 4. COMBAT TRANSITION
 *    - Boss instance is wrapped in EncounterEnemyConfig
 *    - ScreenManager routes to ENCOUNTER screen with boss encounter
 *    - Combat proceeds normally with boss as enemy
 *    - Boss has visual differentiation (larger avatar, "Boss" label)
 *
 * 5. POST-BOSS ROUTING
 *    - Boss victory (floors 1-2): → REWARD screen → next floor
 *    - Boss victory (floor 3): → VICTORY screen (run complete)
 *    - Boss defeat: → DEFEAT screen → restart option
 *
 * State Management:
 * - RunState.isBossEncounter: boolean flag indicating boss fight
 * - RunState.currentFloorConfig.bossCandidate: string ID of boss for current floor
 * - Boss instances are created on-demand, not stored in RunState
 *
 * Generation:
 * - Bosses are generated via BossFactory.create(bossId, { floor })
 * - Scaling uses getEnemyScalingMultipliers(floor) * 1.5 for HP/ATK
 * - Perks are applied after scaling (base → scale → perks sequence)
 *
 * Boss Perks & Abilities:
 * - Bosses use a combination of regular perks and boss-specific perks
 * - Boss-specific perks include:
 *   - perk_multi_attack: Increases attack power to simulate attacking twice
 *   - perk_boss_reflect: Reflects 2 damage per stack (stronger than thorns)
 *   - perk_boss_heal: Heals 3 HP per stack when taking damage (stronger than adrenaline)
 * - Boss perks integrate with existing combat hooks (onDamageDealt, onDamageTaken)
 * - Perk effects stack multiplicatively (multiple stacks = stronger effect)
 * - Boss perks are applied during BossFactory.create() via applyPerks()
 */

/**
 * Boss ID type - extends EnemyId to include boss variants.
 * Boss IDs follow the pattern "boss_<base_enemy>" or unique boss names.
 */
export type BossId =
  | "boss_bat"
  | "boss_goblin"
  | "boss_slime"
  | "boss_spider"
  | "boss_skeleton"
  | "boss_treant"
  | "boss_mimic"
  | "boss_dragon"
  | "boss_champion";

/**
 * Boss definition interface.
 * Bosses are enhanced enemies with higher stats, special perks, and unique abilities.
 * They follow the same pattern as EnemyDef but with boss-specific scaling.
 */
export type BossDef = {
  id: BossId;
  name: string;
  /** Base max HP (will be scaled by floor level) */
  maxHp: number;
  /** Base attack (will be scaled by floor level) */
  atk: number;
  /** Base armor */
  armor: number;
  /** Probability (0-1) that boss will defend instead of attack. Default 0. */
  defendChance?: number;
  /** Boss-specific perks that make them unique */
  perks?: PerkId[];
  /** Base enemy type this boss is based on (for visual blueprint reference) */
  baseEnemyId: EnemyId;
};

/**
 * Boss configuration registry.
 * Maps boss IDs to their definitions.
 * Bosses scale with floor level and have unique perk combinations.
 */
export const BOSSES: Record<BossId, BossDef> = {
  // Floor 1 bosses (introductory)
  boss_bat: {
    id: "boss_bat",
    name: "Bat Swarm Leader",
    maxHp: 40,
    atk: 6,
    armor: 2,
    defendChance: 0.2,
    perks: ["perk_lifesteal", "perk_adrenaline"],
    baseEnemyId: "bat",
  },
  boss_goblin: {
    id: "boss_goblin",
    name: "Goblin Chief",
    maxHp: 50,
    atk: 8,
    armor: 8,
    defendChance: 0.3,
    perks: ["perk_rage", "perk_armor"],
    baseEnemyId: "goblin",
  },
  boss_slime: {
    id: "boss_slime",
    name: "Slime King",
    maxHp: 60,
    atk: 5,
    armor: 5,
    defendChance: 0.25,
    perks: ["perk_thorns", "perk_hp"],
    baseEnemyId: "slime",
  },
  // Floor 2 bosses (mid-difficulty)
  boss_spider: {
    id: "boss_spider",
    name: "Spider Queen",
    maxHp: 70,
    atk: 10,
    armor: 4,
    defendChance: 0.25,
    perks: ["perk_boss_reflect", "perk_lifesteal", "perk_rage"],
    baseEnemyId: "spider",
  },
  boss_skeleton: {
    id: "boss_skeleton",
    name: "Lich",
    maxHp: 80,
    atk: 12,
    armor: 6,
    defendChance: 0.2,
    perks: ["perk_lifesteal", "perk_atk", "perk_boss_heal"],
    baseEnemyId: "skeleton",
  },
  boss_treant: {
    id: "boss_treant",
    name: "Ancient Treant",
    maxHp: 90,
    atk: 9,
    armor: 10,
    defendChance: 0.35,
    perks: ["perk_thorns", "perk_armor", "perk_hp"],
    baseEnemyId: "goblin", // Using goblin blueprint as placeholder
  },
  // Floor 3 bosses (challenging/climactic)
  boss_mimic: {
    id: "boss_mimic",
    name: "Mimic Chest",
    maxHp: 100,
    atk: 14,
    armor: 8,
    defendChance: 0.3,
    perks: ["perk_boss_reflect", "perk_rage", "perk_multi_attack"],
    baseEnemyId: "goblin", // Using goblin blueprint as placeholder (will use chest blueprint)
  },
  boss_dragon: {
    id: "boss_dragon",
    name: "Dragon",
    maxHp: 120,
    atk: 16,
    armor: 6,
    defendChance: 0.2,
    perks: ["perk_lifesteal", "perk_rage", "perk_multi_attack", "perk_boss_heal"],
    baseEnemyId: "goblin", // Using goblin blueprint as placeholder
  },
  boss_champion: {
    id: "boss_champion",
    name: "Champion",
    maxHp: 110,
    atk: 15,
    armor: 10,
    defendChance: 0.3,
    perks: ["perk_rage", "perk_armor", "perk_multi_attack", "perk_boss_reflect"],
    baseEnemyId: "goblin", // Using goblin blueprint as placeholder
  },
};

/**
 * Get boss definition by ID.
 * @param bossId - The boss ID
 * @returns BossDef or throws if not found
 */
export function getBossDef(bossId: BossId): BossDef {
  const boss = BOSSES[bossId];
  if (!boss) {
    throw new Error(`Unknown boss ID: ${bossId}`);
  }
  return boss;
}
